/**
 * Styling Settings Component
 *
 * Component for managing popup styling and appearance.
 *
 * @since 1.0.0
 */

import type { BoxInputValues } from '@redfoxwp/core-components/components/box-input';
import { BoxInput } from '@redfoxwp/core-components/components/box-input';
import { Card } from '@redfoxwp/core-components/components/card';
import { Flex } from '@redfoxwp/core-components/components/flex';
import { FlexItem } from '@redfoxwp/core-components/components/flex-item';
import { LoadingSpinner } from '@redfoxwp/core-components/components/loading-spinner';
import { SettingWrapper } from '@redfoxwp/core-components/components/setting-wrapper';
import { __ } from '@wordpress/i18n';

import { useSettingsContext } from '../../../contexts/settings-context';

/**
 * Styling Settings component
 *
 * @since 1.0.0
 *
 * @return JSX element.
 */
export const StylingSettings = (): JSX.Element => {
  const { settings, loading, isSaving, isResetting, updateSettings } = useSettingsContext();

  const handleBorderRadiusChange = (values: BoxInputValues) => {
    updateSettings('styling', {
      ...settings?.styling,
      border_radius_values: values,
    });
  };

  // Show loading spinner only on initial data load
  if (loading) {
    return (
      <Card
        styleProps={{
          padding: '30px',
        }}
      >
        <LoadingSpinner
          isLoading={true}
          variant='ring'
          size='medium'
          centered={true}
          showText={true}
          text={__('Loading settings...', 'redfox-added-to-cart-popup')}
        />
      </Card>
    );
  }

  // Disable inputs during save/reset operations
  const isDisabled = isSaving || isResetting;

  return (
    <Flex direction='column' gap='20px' fullWidth>
      <FlexItem>
        <SettingWrapper
          label={__('Border Radius', 'redfox-added-to-cart-popup')}
          description={__(
            'Corner radius of the popup border. Use the link button to maintain uniform radius across all corners.',
            'redfox-added-to-cart-popup'
          )}
          styleProps={{
            containerPadding: '30px',
          }}
        >
          <BoxInput
            layout='corners'
            size='medium'
            values={
              settings?.styling?.border_radius_values ?? {
                top: 0,
                right: 0,
                bottom: 0,
                left: 0,
                unit: 'px',
                linked: true,
              }
            }
            onChange={handleBorderRadiusChange}
            min={0}
            max={50}
            step={1}
            allowNegative={false}
            showControls={true}
            unitOptions={[
              { value: 'px', label: 'px', abbreviation: 'px' },
              { value: 'em', label: 'em', abbreviation: 'em' },
              { value: 'rem', label: 'rem', abbreviation: 'rem' },
              { value: '%', label: '%', abbreviation: '%' },
            ]}
            labels={{
              top: __('Top Left', 'redfox-added-to-cart-popup'),
              right: __('Top Right', 'redfox-added-to-cart-popup'),
              bottom: __('Bottom Right', 'redfox-added-to-cart-popup'),
              left: __('Bottom Left', 'redfox-added-to-cart-popup'),
            }}
            disabled={isDisabled}
          />
        </SettingWrapper>
      </FlexItem>
    </Flex>
  );
};
